import React, { useEffect, useState, useRef } from "react";
import {
  getScrapingProgress,
  ScrapingProgressData,
} from "../../services/scrapingProgress";
import AppLoader from "../../components/common/AppLoader";
import "./style.css";

const STARTING_GRACE_MS = 20000; // Show "Starting..." for 20s if still idle
const POLL_INTERVAL_IN_PROGRESS_MS = 1000;
const POLL_INTERVAL_IDLE_MS = 5000;
const REDIRECT_DELAY_MS = 2000;

interface ScrapingProgressPageProps {
  onComplete?: () => void;
}

const ScrapingProgressPage: React.FC<ScrapingProgressPageProps> = ({ onComplete }) => {
  const [progressData, setProgressData] = useState<ScrapingProgressData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [completionTriggered, setCompletionTriggered] = useState(false);
  const hasSeenInProgress = useRef(false);
  const mountedAt = useRef(Date.now());

  const fetchProgress = async () => {
    try {
      const data = await getScrapingProgress();
      setProgressData(data);
      setLoading(false);

      if (data.overallStatus === "in_progress") {
        hasSeenInProgress.current = true;
      }

      // Auto-redirect only after we have seen scraping run (in_progress) and then it has finished.
      // This avoids redirecting on page load when status is already "completed" or "idle" from a previous run.
      const scrapingJustFinished =
        hasSeenInProgress.current &&
        ((data.overallStatus === "completed" && data.inProgressCount === 0) ||
          data.overallStatus === "idle");
      if (scrapingJustFinished && !completionTriggered) {
        setCompletionTriggered(true);
        setTimeout(() => {
          onComplete?.();
        }, REDIRECT_DELAY_MS);
      }
    } catch (err: any) {
      setError(err.message || "Failed to fetch scraping progress");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProgress();
  }, []);

  useEffect(() => {
    if (loading || completionTriggered) return;
    const isInProgress = progressData?.overallStatus === "in_progress";
    const intervalMs = isInProgress ? POLL_INTERVAL_IN_PROGRESS_MS : POLL_INTERVAL_IDLE_MS;
    const interval = setInterval(fetchProgress, intervalMs);
    return () => clearInterval(interval);
  }, [loading, completionTriggered, progressData?.overallStatus]);

  if (loading) {
    return <AppLoader />;
  }

  if (error) {
    return (
      <div className="scraping-progress-container">
        <div className="progress-card error-card">
          <h2>❌ Error</h2>
          <p>{error}</p>
          <button className="btn-primary" onClick={() => onComplete?.()}>
            Go to Products
          </button>
        </div>
      </div>
    );
  }

  if (!progressData) {
    return <AppLoader />;
  }

  const { overallStatus, overallProgress, currentOperation } = progressData;

  const isWithinStartingGrace = Date.now() - mountedAt.current < STARTING_GRACE_MS;
  const showStartingMessage = overallStatus === "idle" && isWithinStartingGrace;

  if (overallStatus === "idle") {
    return (
      <div className="scraping-progress-container">
        <div className="progress-card">
          <div className="progress-header">
            <h1>{showStartingMessage ? "🔄 Starting scrape…" : "⏸️ No Active Scraping"}</h1>
            <p className="current-operation">
              {showStartingMessage
                ? "Waiting for scraper to start. This may take a few seconds…"
                : "The scraper is currently idle"}
            </p>
          </div>
          {showStartingMessage ? (
            <div className="completion-message">
              <AppLoader />
              <p className="redirect-message">Polling for progress…</p>
            </div>
          ) : (
            <>
              <div className="completion-message">
                <p>No scraping operation is currently running.</p>
                <p className="redirect-message">
                  Start a scrape from the Websites tab to see progress here.
                </p>
              </div>
              <div className="action-buttons">
                <button className="btn-primary" onClick={() => onComplete?.()}>
                  Go to Products Table
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="scraping-progress-container">
      <div className="progress-card">
        <div className="progress-header">
          <h1>
            {overallStatus === "in_progress" && "🔄 Scraping in Progress"}
            {overallStatus === "completed" && "✅ Scraping Completed"}
            {overallStatus === "failed" && "❌ Scraping Failed"}
          </h1>
          <p className="current-operation">
            {overallStatus === "in_progress" ||
            overallStatus === "completed" ||
            overallStatus === "failed"
              ? `(${progressData.processedUrls ?? 0}/${progressData.totalUrls ?? 0} URLs)`
              : currentOperation}
          </p>
        </div>

        <div className="progress-bar-container">
          <div className="progress-bar-wrapper">
            <div
              className="progress-bar-fill"
              style={{
                width: `${overallProgress}%`,
                backgroundColor:
                  overallStatus === "completed"
                    ? "#4FD1C5"
                    : overallStatus === "failed"
                    ? "#ef4444"
                    : "#4FD1C5",
              }}
            >
              {overallProgress > 10 && (
                <span className="progress-percentage">{overallProgress}%</span>
              )}
            </div>
          </div>
          {overallProgress <= 10 && (
            <span className="progress-percentage-outside">
              {overallProgress}%
            </span>
          )}
        </div>

        <div className="progress-stats">
          <div className="stat-item">
            <span className="stat-label">Total URLs:</span>
            <span className="stat-value">
              {progressData.processedUrls} / {progressData.totalUrls}
            </span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Websites:</span>
            <span className="stat-value">
              {progressData.completedCount} /{" "}
              {progressData.participatingWebsitesCount ?? progressData.totalWebsites}{" "}
              completed
            </span>
          </div>
        </div>

        {overallStatus === "completed" && (
          <div className="completion-message">
            <p>✨ Scraping completed successfully!</p>
            <p className="redirect-message">
              {completionTriggered
                ? "Redirecting to products table…"
                : "You can go to the products table below or wait to be redirected."}
            </p>
          </div>
        )}

        <div className="websites-progress">
          <h3>Website Details</h3>
          <div className="websites-grid">
            {progressData.websites.map((website) => (
              <div
                key={website.id}
                className={`website-card ${website.scrapingStatus}`}
              >
                <div className="website-header">
                  <h4>{website.name}</h4>
                  <span
                    className={`status-badge ${website.scrapingStatus}`}
                  >
                    {website.scrapingStatus === "in_progress" && "🔄"}
                    {website.scrapingStatus === "completed" && "✅"}
                    {website.scrapingStatus === "failed" && "❌"}
                    {website.scrapingStatus === "idle" && "⏸️"}
                  </span>
                </div>

                <div className="website-progress-bar">
                  <div
                    className="website-progress-fill"
                    style={{
                      width: `${website.scrapingProgress}%`,
                      backgroundColor:
                        website.scrapingStatus === "completed"
                          ? "#4FD1C5"
                          : website.scrapingStatus === "failed"
                          ? "#ef4444"
                          : "#4FD1C5",
                    }}
                  />
                </div>

                <div className="website-stats">
                  <div className="website-stat">
                    <span>Progress:</span>
                    <strong>{website.scrapingProgress}%</strong>
                  </div>
                  <div className="website-stat">
                    <span>URLs:</span>
                    <strong>
                      {website.processedUrls} / {website.totalUrls}
                    </strong>
                  </div>
                  {website.scrapingStatus === "completed" && (
                    <>
                      <div className="website-stat">
                        <span>Success:</span>
                        <strong>{website.itemsScraped}</strong>
                      </div>
                      <div className="website-stat">
                        <span>Errors:</span>
                        <strong>{website.errorCount}</strong>
                      </div>
                      <div className="website-stat">
                        <span>Success Rate:</span>
                        <strong>{website.successRate}%</strong>
                      </div>
                    </>
                  )}
                </div>

                {website.currentOperation && website.scrapingStatus === "in_progress" && (
                  <p className="website-operation">{website.currentOperation}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="action-buttons">
          {overallStatus === "completed" && (
            <button
              className="btn-primary"
              onClick={() => onComplete && onComplete()}
            >
              View Products Table
            </button>
          )}
          {overallStatus === "in_progress" && (
            <button
              className="btn-secondary"
              onClick={() => onComplete && onComplete()}
            >
              View Products (Scraping in Background)
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScrapingProgressPage;
